import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import {
  fetchForecast,
  fetchForecastMonthToDate,
  fetchForecastWhatIf,
} from '@src/api/actions/dashboard/forecast';
import {
  MonthToDateView,
  PortfolioBanner,
  ScenarioSummary,
  SegmentTable,
  usd0,
  pct,
} from './components/forecast/ForecastWidgets';
import {
  BarsBanner,
  InMonthPanel,
  ReallocationPanel,
  SpendLadder,
} from './components/forecast/ForecastPlanWidgets';
import ClientForecastView, { NOT_AVAILABLE_MESSAGE } from './components/forecast/ClientForecastView';
import ForecastAccessPanel from './components/forecast/ForecastAccessPanel';
import { daysInPeriod, localWhatIf } from './components/forecast/forecastMath';

const WHATIF_DEBOUNCE_MS = 300;

const budgetsKey = (segments, b) =>
  (segments || []).map((s) => `${s.budget_id}:${Number(b[s.budget_id])}`).join('|');

const DIRECTION_COUNT_LABEL = {
  increase: 'Scale up',
  pull_back: 'Pull back',
  at_capacity: 'At capacity',
  hold: 'On track',
};

export default function ForecastPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useEffectiveUser();
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  // A client member (or staff previewing as one via the client-view switcher)
  // lands on their own client when the link carries no client_id.
  const memberClientId = !isStaff ? user?.client_memberships?.[0]?.client_id : null;
  const clientId = searchParams.get('client_id') || (memberClientId ? String(memberClientId) : '');
  // Staff in the app-wide client preview are still staff to the API, so ask
  // for the client view explicitly.
  const viewParam = user?.is_client_preview ? { view: 'client' } : {};
  const parentId = searchParams.get('parent_company_id') || '';

  const {
    options: clientOptions,
    loading: clientLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue,
    fetchClients,
  } = useClientAutocomplete({});
  const [selectedClientOption, setSelectedClientOption] = useState(null);

  // Pre-load the client list so the picker isn't empty before the user types.
  useEffect(() => {
    if (isStaff && fetchClients) fetchClients('');
  }, [isStaff, fetchClients]);

  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [tab, setTab] = useState('next');
  const [mtd, setMtd] = useState(null);
  const [mtdLoading, setMtdLoading] = useState(false);

  const [verticalFilter, setVerticalFilter] = useState('all');

  const [budgets, setBudgets] = useState({}); // budget_id -> monthly $
  const [whatif, setWhatif] = useState(null);
  const [whatifLoading, setWhatifLoading] = useState(false);
  const [whatifError, setWhatifError] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const timerRef = useRef(null);
  // Only the latest what-if response may land; an older one finishing late
  // would otherwise overwrite the numbers for the slider's current position.
  const whatifSeqRef = useRef(0);

  // Staff: render exactly what the client sees, below the staff view.
  const [previewClient, setPreviewClient] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState('');

  const isParent = Boolean(parentId);

  // Reflect a deep-linked client_id back into the picker label once options load.
  useEffect(() => {
    if (clientId && !selectedClientOption && findOptionByValue) {
      const opt = findOptionByValue(clientId);
      if (opt) setSelectedClientOption(opt);
    }
  }, [clientId, selectedClientOption, findOptionByValue, clientOptions]);

  // ---- load the baseline forecast (single client or parent rollup) --------
  useEffect(() => {
    if (!clientId && !parentId) {
      setForecast(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setWhatif(null);
    setWhatifError(false);
    whatifSeqRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    setVerticalFilter('all');
    const params = parentId ? { parent_company_id: parentId } : { client_id: clientId, ...viewParam };
    fetchForecast(params)
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data || resp;
        setForecast(data);
        const init = {};
        (data?.segments || []).forEach((s) => {
          init[s.budget_id] = s.base_budget;
        });
        setBudgets(init);
        const firstId = data?.recommendations?.[0]?.budget_id ?? data?.segments?.[0]?.budget_id;
        setExpanded(new Set(!parentId && firstId != null ? [firstId] : []));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.status === 403
            ? NOT_AVAILABLE_MESSAGE
            : 'Could not load the forecast.'
        );
        setForecast(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // viewParam is derived from user.is_client_preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, parentId, user?.is_client_preview]);

  // ---- staff preview of the client view -----------------------------------
  useEffect(() => {
    if (!isStaff || !previewClient || !clientId || parentId) {
      setPreviewData(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewError('');
    fetchForecast({ client_id: clientId, view: 'client' })
      .then((resp) => !cancelled && setPreviewData(resp?.data || resp))
      .catch(() => !cancelled && setPreviewError('Could not load the client view.'));
    return () => {
      cancelled = true;
    };
  }, [isStaff, previewClient, clientId, parentId]);

  // ---- month-to-date tab --------------------------------------------------
  useEffect(() => {
    if (tab !== 'mtd' || !clientId || isParent) return undefined;
    let cancelled = false;
    setMtdLoading(true);
    fetchForecastMonthToDate({ client_id: clientId, ...viewParam })
      .then((resp) => !cancelled && setMtd(resp?.data || resp))
      .catch(() => !cancelled && setMtd(null))
      .finally(() => !cancelled && setMtdLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, clientId, isParent, user?.is_client_preview]);

  // ---- what-if: priced instantly in the browser, reconciled by the server --
  // The slider drives ``budgets``; ``localWhatif`` reprices on every move with
  // the server's own math (forecastMath.js), then the debounced POST confirms.
  // A server answer is shown only while it still matches the current budgets.
  const runWhatIf = useCallback(
    (nextBudgets) => {
      if (!forecast || isParent) return;
      const seq = ++whatifSeqRef.current;
      const overrides = (forecast.segments || [])
        .filter((s) => Number(nextBudgets[s.budget_id]) !== Number(s.base_budget))
        .map((s) => ({ budget_id: s.budget_id, budget: nextBudgets[s.budget_id] }));
      if (overrides.length === 0) {
        setWhatif(null);
        setWhatifError(false);
        setWhatifLoading(false);
        return;
      }
      const key = budgetsKey(forecast.segments, nextBudgets);
      setWhatifLoading(true);
      fetchForecastWhatIf({ client_id: clientId, period: forecast.period, overrides })
        .then((resp) => {
          if (seq !== whatifSeqRef.current) return;
          setWhatif({ ...(resp?.data || resp), _key: key });
          setWhatifError(false);
        })
        .catch(() => {
          if (seq !== whatifSeqRef.current) return;
          setWhatifError(true);
        })
        .finally(() => {
          if (seq === whatifSeqRef.current) setWhatifLoading(false);
        });
    },
    [forecast, clientId, isParent]
  );

  const handleSlider = useCallback(
    (budgetId, value) => {
      if (isParent) return;
      setBudgets((prev) => {
        const next = { ...prev, [budgetId]: value };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => runWhatIf(next), WHATIF_DEBOUNCE_MS);
        return next;
      });
    },
    [runWhatIf, isParent]
  );

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const init = {};
    (forecast?.segments || []).forEach((s) => {
      init[s.budget_id] = s.base_budget;
    });
    setBudgets(init);
    setWhatif(null);
    setWhatifError(false);
    setWhatifLoading(false);
    whatifSeqRef.current += 1;
  }, [forecast]);

  // ---- derived ------------------------------------------------------------
  const dirty = useMemo(
    () =>
      (forecast?.segments || []).some(
        (s) => Number(budgets[s.budget_id]) !== Number(s.base_budget)
      ),
    [budgets, forecast]
  );

  const daysInMonth = useMemo(() => daysInPeriod(forecast?.period), [forecast]);

  const localWhatif = useMemo(() => {
    if (!dirty || isParent || !forecast?.segments) return null;
    return localWhatIf(forecast.segments, budgets, daysInMonth);
  }, [dirty, isParent, forecast, budgets, daysInMonth]);

  // Server answer only while it matches the sliders; otherwise the instant one.
  const currentKey = useMemo(() => budgetsKey(forecast?.segments, budgets), [budgets, forecast]);
  const serverWhatif = dirty && whatif && whatif._key === currentKey ? whatif : null;
  const activeWhatif = serverWhatif || localWhatif;

  const displayScenarios = activeWhatif?.scenarios || forecast?.scenarios;

  const outputsById = useMemo(() => {
    const src = activeWhatif?.segments || forecast?.segments || [];
    const map = {};
    src.forEach((s) => {
      map[s.budget_id] = s.outputs;
    });
    return map;
  }, [activeWhatif, forecast]);

  const recById = useMemo(() => {
    const map = {};
    const recs = isParent
      ? (forecast?.children || []).flatMap((c) => c.recommendations || [])
      : forecast?.recommendations || [];
    recs.forEach((r) => {
      map[r.budget_id] = r;
    });
    return map;
  }, [forecast, isParent]);

  const orderSegments = useCallback((segments, recommendations) => {
    const byId = {};
    (segments || []).forEach((s) => {
      byId[s.budget_id] = s;
    });
    const ordered = [];
    (recommendations || []).forEach((r) => {
      if (byId[r.budget_id]) {
        ordered.push(byId[r.budget_id]);
        delete byId[r.budget_id];
      }
    });
    Object.values(byId).forEach((s) => ordered.push(s));
    return ordered;
  }, []);

  const orderedSegments = useMemo(
    () => (isParent ? [] : orderSegments(forecast?.segments, forecast?.recommendations)),
    [forecast, isParent, orderSegments]
  );

  const verticals = useMemo(() => {
    const names = new Set(
      (forecast?.segments || []).map((s) => s.vertical).filter(Boolean)
    );
    return [...names];
  }, [forecast]);

  const filteredSegments = useMemo(
    () =>
      verticalFilter === 'all'
        ? orderedSegments
        : orderedSegments.filter((s) => (s.vertical || '') === verticalFilter),
    [orderedSegments, verticalFilter]
  );

  // Group by vertical (with subtotals) when the client runs several verticals
  // and no filter is applied.
  const groupedSegments = useMemo(() => {
    if (isParent || verticalFilter !== 'all' || verticals.length < 2) return null;
    const groups = [];
    const seen = new Map();
    filteredSegments.forEach((s) => {
      const key = s.vertical || 'Other';
      if (!seen.has(key)) {
        seen.set(key, { vertical: key, segments: [] });
        groups.push(seen.get(key));
      }
      seen.get(key).segments.push(s);
    });
    return groups;
  }, [filteredSegments, isParent, verticalFilter, verticals]);

  const groupSubtotal = useCallback(
    (segments) => {
      let budget = 0;
      let revenue = 0;
      let delta = 0;
      segments.forEach((s) => {
        budget += Number(budgets[s.budget_id] ?? s.base_budget) || 0;
        const out = (outputsById[s.budget_id] || s.outputs || {}).expected || {};
        revenue += out.net_revenue || 0;
        delta += recById[s.budget_id]?.recommended_delta || 0;
      });
      const com = revenue ? budget / revenue : null;
      return { budget, revenue, com, delta };
    },
    [budgets, outputsById, recById]
  );

  const periodLabel = forecast?.period
    ? new Date(`${forecast.period}T00:00:00`).toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      })
    : '';

  const renderSegmentTable = (segments) => (
    <SegmentTable
      segments={segments}
      recById={recById}
      outputsById={outputsById}
      budgets={budgets}
      goalCom={forecast?.goal_com}
      daysInMonth={daysInMonth}
      expanded={expanded}
      onToggle={toggleExpand}
      onSlider={handleSlider}
    />
  );

  // ---- render -------------------------------------------------------------
  // Client members (and staff previewing as one) get the client-safe view.
  if (!isStaff || forecast?.view === 'client') {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', minWidth: 0 }}>
        {error && <Alert severity="info" sx={{ mb: 2 }}>{error}</Alert>}
        {!clientId && !error && (
          <Alert severity="info">{NOT_AVAILABLE_MESSAGE}</Alert>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && forecast && <ClientForecastView data={forecast} clientId={clientId} />}
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Forecast
          </Typography>
          {periodLabel && (
            <Typography variant="body2" color="text.secondary">
              {isParent ? 'Portfolio view — projecting' : 'Projecting'} {periodLabel} from recent performance
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {isStaff && !isParent && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 280 }}
              loading={clientLoading}
              options={clientOptions}
              value={selectedClientOption}
              onChange={(_e, option) => {
                setSelectedClientOption(option || null);
                setSearchParams(option?.value ? { client_id: String(option.value) } : {});
              }}
              onInputChange={handleClientSearch}
              getOptionLabel={(o) => o?.label || ''}
              isOptionEqualToValue={(o, v) => String(o?.value) === String(v?.value)}
              renderOption={(props, option) => (
                <li {...props} key={option?.value ?? option?.label}>{option?.label || ''}</li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Search clients…" />
              )}
            />
          )}
          {dirty && !isParent && (
            <Button size="small" onClick={handleReset} variant="outlined">
              Reset
            </Button>
          )}
        </Stack>
      </Stack>

      {!isParent && clientId && (
        <ForecastAccessPanel
          clientId={clientId}
          preview={previewClient}
          onPreviewChange={setPreviewClient}
        />
      )}

      {previewClient && !isParent && clientId && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.5, md: 2.5 }, mb: 3, borderRadius: 2, borderStyle: 'dashed', borderColor: 'primary.main' }}
        >
          <Typography variant="overline" color="primary">
            Client view preview — exactly what this client&apos;s users see
          </Typography>
          {previewError && <Alert severity="warning">{previewError}</Alert>}
          {!previewData && !previewError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {previewData && <ClientForecastView data={previewData} clientId={clientId} />}
        </Paper>
      )}

      {!isParent && clientId && (
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="next" label="Next month" />
          <Tab value="mtd" label="This month" />
        </Tabs>
      )}

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {!clientId && !parentId && !error && (
        <Alert severity="info">Select a client to see their forecast.</Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* ---- This month (single client) ---- */}
      {!loading && tab === 'mtd' && !isParent && clientId && (
        mtdLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <MonthToDateView data={mtd} />
            {/* Grading the month is only half of it — the other half is what to
                do with the days that are left. */}
            {mtd?.in_month && <InMonthPanel plan={mtd.in_month} />}
          </Stack>
        )
      )}

      {/* ---- Next month: parent portfolio ---- */}
      {!loading && tab === 'next' && isParent && forecast && (
        <Stack spacing={3}>
          <Box>
            <ScenarioSummary scenarios={forecast.scenarios} />
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {Object.entries(forecast.direction_counts || {}).map(([dir, count]) => (
                <Chip
                  key={dir}
                  size="small"
                  variant="outlined"
                  label={`${count} ${DIRECTION_COUNT_LABEL[dir] || dir}`}
                />
              ))}
              {Boolean(forecast.recommended_total_delta) && (
                <Chip
                  size="small"
                  color={forecast.recommended_total_delta > 0 ? 'success' : 'warning'}
                  label={`Recommended net change ${forecast.recommended_total_delta > 0 ? '+' : ''}${usd0(forecast.recommended_total_delta)}/mo`}
                />
              )}
            </Stack>
          </Box>
          {(forecast.children || []).map((child) => {
            const childSegments = orderSegments(child.segments, child.recommendations);
            return (
              <Box key={child.client_id}>
                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{child.client_name}</Typography>
                  {child.portfolio?.blended_com != null && (
                    <Typography variant="caption" color="text.secondary">
                      blended CoM {pct(child.portfolio.blended_com)} vs {pct(child.goal_com)}
                    </Typography>
                  )}
                </Stack>
                {renderSegmentTable(childSegments)}
              </Box>
            );
          })}
        </Stack>
      )}

      {/* ---- Next month: single client ---- */}
      {!loading && tab === 'next' && !isParent && forecast && (
        <Stack spacing={3}>
          <Box>
            <ScenarioSummary scenarios={displayScenarios} goalCom={forecast.goal_com} />
            {dirty && whatifError && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.75, display: 'block' }}>
                Couldn&apos;t confirm with the server; showing the in-browser estimate.
              </Typography>
            )}
            {forecast.bars ? (
              <BarsBanner bars={forecast.bars} portfolio={forecast.portfolio} />
            ) : (
              <PortfolioBanner portfolio={forecast.portfolio} goalSource={forecast.goal_source} />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Low / expected / high based on this client&apos;s own recent data — actual results vary.
            </Typography>
          </Box>

          {/* The two questions clients actually ask, before the per-market
              detail: what is the best use of what they already gave us, and
              what would more (or less) do. */}
          {forecast.reallocation && <ReallocationPanel plan={forecast.reallocation} />}
          {forecast.ladder && (
            <SpendLadder
              ladder={forecast.ladder}
              clientId={clientId}
              period={forecast.period}
            />
          )}

          <Divider />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                By segment
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {whatifLoading && <CircularProgress size={16} />}
                {verticals.length > 1 && (
                  <TextField
                    select
                    size="small"
                    label="Vertical"
                    value={verticalFilter}
                    onChange={(e) => setVerticalFilter(e.target.value)}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="all">All verticals</MenuItem>
                    {verticals.map((v) => (
                      <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Sorted by what needs attention first. Click a segment to see our recommendation, a budget
              slider, and how efficiency changes as you scale.
            </Typography>
            {groupedSegments ? (
              <Stack spacing={2.5}>
                {groupedSegments.map((group) => {
                  const sub = groupSubtotal(group.segments);
                  return (
                    <Box key={group.vertical}>
                      <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 0.75 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {group.vertical}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {usd0(sub.budget)}/mo · projected {usd0(sub.revenue)} net revenue
                          {sub.com != null ? ` · blended CoM ${pct(sub.com)}` : ''}
                          {sub.delta
                            ? ` · recommended ${sub.delta > 0 ? '+' : ''}${usd0(sub.delta)}/mo`
                            : ''}
                        </Typography>
                      </Stack>
                      {renderSegmentTable(group.segments)}
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              renderSegmentTable(filteredSegments)
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
